import { useCallback, useRef } from "react";
import { routePrefetchMap } from "../config/routePrefetchMap";

/** Set of route paths whose chunks have already been prefetched */
const prefetchedRoutes = new Set<string>();

/** Pending prefetch timer refs keyed by path */
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Debounce delay in ms before triggering the prefetch import */
const PREFETCH_DEBOUNCE_MS = 100;

/**
 * Prefetch the lazy-loaded chunk for a given route path.
 * Uses the routePrefetchMap from lazyPages config.
 * Results are cached so each chunk is only fetched once.
 *
 * @returns The prefetch function and the set of already-prefetched routes.
 */
export function usePrefetch() {
  const activeRef = useRef(true);

  const prefetch = useCallback((path: string) => {
    // Already prefetched - skip
    if (prefetchedRoutes.has(path)) return;

    // Already has a pending timer - skip
    if (pendingTimers.has(path)) return;

    // Normalise path: strip trailing slash, take the base path for dynamic segments
    const normalised = normalisePath(path);

    const importFn = routePrefetchMap[normalised];
    if (!importFn) return;

    // Debounce to avoid excessive loads when user quickly moves across links
    const timer = setTimeout(() => {
      pendingTimers.delete(path);

      if (!activeRef.current) return;
      if (prefetchedRoutes.has(path)) return;

      prefetchedRoutes.add(path);

      // Fire the dynamic import - this loads the chunk into browser cache
      importFn().catch(() => {
        // Remove from cache on failure so it can be retried
        prefetchedRoutes.delete(path);
      });
    }, PREFETCH_DEBOUNCE_MS);

    pendingTimers.set(path, timer);
  }, []);

  const cancelPrefetch = useCallback((path: string) => {
    const timer = pendingTimers.get(path);
    if (timer) {
      clearTimeout(timer);
      pendingTimers.delete(path);
    }
  }, []);

  return {
    prefetch,
    cancelPrefetch,
    prefetchedRoutes,
  };
}

/**
 * Normalise a route path for lookup in the prefetch map.
 * Strips trailing slashes and resolves common dynamic segments.
 */
function normalisePath(path: string): string {
  // Remove trailing slash
  const normalised = path.endsWith("/") && path.length > 1
    ? path.slice(0, -1)
    : path;

  // For paths like /meetups/123 (dynamic ID), map to the base route
  // but preserve known sub-paths like /meetups/new, /meetups/create
  const knownSubPaths = [
    "/meetups/new",
    "/meetups/new/wizard",
    "/meetups/create",
  ];
  if (
    normalised.startsWith("/meetups/") &&
    !knownSubPaths.includes(normalised) &&
    !normalised.endsWith("/edit")
  ) {
    // Dynamic meetup route - no prefetch map entry for these
    return normalised;
  }

  return normalised;
}

export default usePrefetch;
